import React, { useEffect } from 'react';
import CinemmerseProSlider from '../components/CinemmerseProSlider';
import { useSEO, seoConfigs } from '../hooks/useSEO';

export const CinemmerseProPage: React.FC = () => {
    useSEO(seoConfigs.cinemmersePro);
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const callToAction = (
        <div className="mt-8 text-center">
            <button
                disabled
                className="cinematic-button inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-semibold rounded-full text-white bg-zinc-700 cursor-not-allowed opacity-80 shadow-none transition-none"
            >
                Coming Soon
            </button>
            <p className="mt-3 text-sm text-gray-400">Open Source (MIT) · macOS / Windows / Linux</p>
        </div>
    );

    const keyFeatures = [
        'Lossless Transparency: Bit-transparent conversion using SMPTE Digital Lossless Compression (DLC)',
        'Cinematic Aurora UI: High-contrast, glassmorphic design for dark mixing environments',
        'Zero-Config Detection: Auto-detects frame rates, channels, and object pools',
        'Theatrical Lockdown: Enforces Dolby Application Profile 1 constraints for perfect playback',
        'Universal Compatibility: Validated for Barco CP950 and Dolby CP850 processors',
    ];

    const valueProps = [
        'Bridge between studio and cinema',
        'Streamlined, professional alternative',
        'Real-time 128-channel budget monitoring',
        'Native integration with DaVinci Resolve & DCP-o-matic pipelines',
    ];

    const technicalSpecs = [
        'Input: IMF IAB (Immersive Audio Bitstream)',
        'Output: DCI-compliant DCP IAB assets',
        'Standard: SMPTE RDD57:2021 compliance',
        'Channels: Up to 118 dynamic objects + 9.1 bed',
        'License: MIT Open Source',
    ];

    const steps = [
        {
            title: 'Initiation',
            details: 'Select your Source Bitstream (IMF IAB). Cinemmerse Pro will immediately initiate a deep scan of the metadata.',
        },
        {
            title: 'Validation',
            details: 'Check the Main Frame Budget. Ensure your total channel count (Beds + Objects) stays within the DCI 128-channel limit. Our visual budget bar will guide you.',
        },
        {
            title: 'Theatrical Lockdown',
            details: 'Enable "Theatrical Bed Lockdown" (Force Dolby Profile 1) to ensure strict compliance with theatrical server requirements.',
        },
        {
            title: 'Reassembly',
            details: 'Click Start Conversion. Watch the Film Strip progress bar. Your .mxf is ready to be wrapped into a final DCP.',
        },
    ];

    return (
        <div className="min-h-screen text-gray-100 py-16 px-4 sm:px-6 lg:px-8 font-sans selection:bg-blue-500/30">
            <div className="max-w-5xl mx-auto space-y-12">

                {/* Hero Section */}
                <section className="text-center space-y-4">
                    <p className="uppercase tracking-[0.4em] text-xs text-blue-400/80">Utilities · macOS App</p>
                    <h1 className="text-4xl md:text-5xl font-semibold text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-100 to-white">
                        Cinemmerse Pro
                    </h1>
                    <p className="text-lg md:text-xl text-emerald-400 font-medium">
                        Theatrical Delivery & Branding
                    </p>
                    <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        The bridge between the studio and the cinema. A premium audio re-wrapping engine designed to convert IMF IAB masters into DCI-compliant DCP IAB assets.
                    </p>
                    {callToAction}
                </section>

                <CinemmerseProSlider />

                {/* Feature Grid */}
                <section className="bg-zinc-900/60 border border-zinc-800 backdrop-blur rounded-3xl p-8 lg:p-10 shadow-[0_25px_60px_rgba(2,6,23,0.85)]">
                    <div className="grid lg:grid-cols-2 gap-10">
                        <div className="space-y-6">
                            <h2 className="text-2xl font-semibold text-blue-300">The Value Proposition</h2>
                            <p className="text-gray-300 leading-relaxed">
                                While the industry has long been locked into expensive, rigid licensing for immersive delivery, Cinemmerse Pro offers a streamlined, professional alternative that ensures your Atmos® masters play perfectly on standard cinema processors.
                            </p>
                            <ul className="space-y-3">
                                {keyFeatures.map((feature) => (
                                    <li key={feature} className="flex items-start text-sm md:text-base text-gray-200">
                                        <span className="mr-3 mt-1 h-2 w-2 rounded-full bg-blue-400 shrink-0" aria-hidden />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/90 border border-zinc-800 rounded-2xl p-6 flex flex-col justify-between shadow-[0_30px_80px_rgba(0,0,0,0.55)]">
                            <div className="space-y-6">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Why Cinemmerse Pro?</p>
                                    <ul className="mt-3 space-y-2 text-gray-200 text-sm">
                                        {valueProps.map((prop) => (
                                            <li key={prop} className="flex items-start">
                                                <svg className="h-4 w-4 text-emerald-400 mt-1 mr-2 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                {prop}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="pt-4 border-t border-zinc-700/50">
                                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400 mb-3">Baltic Precision</p>
                                    <p className="text-sm text-gray-300 italic">
                                        "Built in Tallinn, Estonia—the heart of European tech innovation."
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-zinc-700/50">
                                <p className="text-xs text-gray-500">
                                    *Supports up to 118 dynamic objects and layered 9.1 bed configurations.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Quotes Section */}
                <section className="grid md:grid-cols-2 gap-6">
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8 relative overflow-hidden group hover:border-blue-500/30 transition-colors">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <svg className="w-16 h-16 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21L14.017 18C14.017 16.896 14.321 16.067 14.929 15.513C15.537 14.959 16.485 14.682 17.773 14.682V12.126C16.486 12.126 15.614 11.54 15.158 10.368C14.702 9.196 14.474 7.625 14.474 5.655L14.502 2H21.97V5.655C21.97 7.625 21.644 9.196 20.992 10.368C20.34 11.54 19.467 12.126 18.374 12.126V14.682C19.662 14.682 20.61 14.959 21.218 15.513C21.826 16.067 22.13 16.896 22.13 18V21H14.017ZM8.003 21L8.003 18C8.003 16.896 8.307 16.067 8.915 15.513C9.523 14.959 10.471 14.682 11.759 14.682V12.126C10.472 12.126 9.6 11.54 9.144 10.368C8.688 9.196 8.46 7.625 8.46 5.655L8.488 2H15.956V5.655C15.956 7.625 15.63 9.196 14.978 10.368C14.326 11.54 13.453 12.126 12.36 12.126V14.682C13.648 14.682 14.596 14.959 15.204 15.513C15.812 16.067 16.116 16.896 16.116 18V21H8.003Z" /></svg>
                        </div>
                        <p className="text-xs uppercase tracking-[0.3em] text-blue-400 mb-4">Studio Sound. Cinema Standards.</p>
                        <blockquote className="text-lg text-gray-200 font-medium leading-relaxed">
                            "The leap from your mixing suite to the theater shouldn’t be a gamble. Cinemmerse Pro provides the technical certainty of SMPTE RDD57 compliance with the elegance of a modern macOS workflow."
                        </blockquote>
                    </div>

                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8 relative overflow-hidden group hover:border-emerald-500/30 transition-colors">
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <svg className="w-16 h-16 text-emerald-400" fill="currentColor" viewBox="0 0 24 24"><path d="M14.017 21L14.017 18C14.017 16.896 14.321 16.067 14.929 15.513C15.537 14.959 16.485 14.682 17.773 14.682V12.126C16.486 12.126 15.614 11.54 15.158 10.368C14.702 9.196 14.474 7.625 14.474 5.655L14.502 2H21.97V5.655C21.97 7.625 21.644 9.196 20.992 10.368C20.34 11.54 19.467 12.126 18.374 12.126V14.682C19.662 14.682 20.61 14.959 21.218 15.513C21.826 16.067 22.13 16.896 22.13 18V21H14.017ZM8.003 21L8.003 18C8.003 16.896 8.307 16.067 8.915 15.513C9.523 14.959 10.471 14.682 11.759 14.682V12.126C10.472 12.126 9.6 11.54 9.144 10.368C8.688 9.196 8.46 7.625 8.46 5.655L8.488 2H15.956V5.655C15.956 7.625 15.63 9.196 14.978 10.368C14.326 11.54 13.453 12.126 12.36 12.126V14.682C13.648 14.682 14.596 14.959 15.204 15.513C15.812 16.067 16.116 16.896 16.116 18V21H8.003Z" /></svg>
                        </div>
                        <p className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">Beyond the Render.</p>
                        <blockquote className="text-lg text-gray-200 font-medium leading-relaxed">
                            "Don't just render your audio. Master it. With real-time 128-channel budget monitoring and the 'Projection Booth' advanced control panel, you are in total command."
                        </blockquote>
                    </div>
                </section>

                {/* Technical Specs & How It Works */}
                <section className="bg-zinc-900/70 border border-zinc-800 rounded-3xl p-8 space-y-8">
                    <div className="flex flex-col md:flex-row gap-8">

                        <div className="md:w-1/3 space-y-4">
                            <p className="text-xs uppercase tracking-[0.3em] text-gray-400">Technical Specs</p>
                            <div className="bg-zinc-950/50 rounded-xl p-4 border border-zinc-800">
                                <ul className="space-y-3 text-sm text-gray-300">
                                    {technicalSpecs.map((spec) => (
                                        <li key={spec} className="flex items-start">
                                            <span className="mr-3 mt-1 h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
                                            {spec}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="md:w-2/3 space-y-6">
                            <div>
                                <p className="text-xs uppercase tracking-[0.3em] text-gray-400">User Manual</p>
                                <h3 className="text-2xl font-semibold text-white mt-1">How It Works</h3>
                            </div>

                            <ol className="space-y-4 text-gray-200">
                                {steps.map((step, index) => (
                                    <li key={step.title} className="flex gap-4">
                                        <div className="text-lg font-semibold text-blue-400 font-mono">0{index + 1}.</div>
                                        <div>
                                            <p className="font-medium text-white text-lg">{step.title}</p>
                                            <p className="text-sm text-gray-400 mt-1 leading-relaxed">{step.details}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    <div className="pt-6 border-t border-zinc-800">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500">
                            <p>Cinemmerse Pro | Built for Professional Cinema Audio.</p>
                            <p>
                                Based on backend developed by{' '}
                                <a
                                    href="https://github.com/izwb003/CineIA_CLI"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-blue-400 transition-colors"
                                >
                                    CineIA
                                </a>
                            </p>
                            <p>A Sangam Panta Project for BFM, Tallinn University.</p>
                        </div>
                    </div>
                </section>

                {/* Footer CTA */}
                <section className="text-center space-y-4 pb-12">
                    {callToAction}
                </section>

            </div>
        </div>
    );
};
